import { Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import { type EventRow, type StayDate, type StayDates, stayDates } from '@/data/source'
import { formatDateTime } from '@/domain/dateTime'

import { useTimeLabels } from './useTimeLabels'

type StayPhase = 'OVERDUE' | 'UNCONFIRMED' | 'DEPARTED' | 'BERTHED' | 'PLANNED'

const phaseOf = (stay: StayDates): StayPhase => {
  if (stay.overdueS !== null) return 'OVERDUE'
  if (stay.unconfirmed) return 'UNCONFIRMED'
  if (stay.departure.actual !== null) return 'DEPARTED'
  if (stay.arrival.actual !== null) return 'BERTHED'
  return 'PLANNED'
}

const PHASE_COLORS = {
  OVERDUE: 'warning',
  UNCONFIRMED: 'default',
  DEPARTED: 'success',
  BERTHED: 'info',
  PLANNED: 'default',
} as const

const useDurations = () => {
  const translate = useTranslate()
  const parts = (seconds: number) => {
    const minutes = Math.round(Math.abs(seconds) / 60)
    return { hours: Math.floor(minutes / 60), minutes: minutes % 60 }
  }
  return {
    signed: (seconds: number): string => translate('pages.scales.stay_dates.signed', { sign: seconds < 0 ? '−' : '+', ...parts(seconds) }),
    ago: (seconds: number): string => translate('pages.scales.stay_dates.ago', parts(seconds)),
  }
}

const COLUMNS = ['planned_first', 'planned_latest', 'actual', 'difference'] as const

const DateCells: FC<{ date: StayDate; testid: string }> = ({ date, testid }) => {
  const translate = useTranslate()
  const { signed } = useDurations()
  const labels = useTimeLabels()
  const time = (value: string | null) => (value === null ? '—' : formatDateTime(value))
  return (
    <>
      <TableCell data-testid={`${testid}-planned-first`}>{time(date.plannedFirst)}</TableCell>
      <TableCell data-testid={`${testid}-planned-latest`}>{time(date.plannedLatest)}</TableCell>
      <TableCell data-testid={`${testid}-actual`} data-source={date.actualSource ?? undefined}>
        {date.actual === null ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic" component="span">
            {translate('pages.scales.stay_dates.not_recorded')}
          </Typography>
        ) : (
          <>
            {formatDateTime(date.actual)}
            <Typography variant="caption" color="text.secondary" display="block">
              {labels.actual(date.actualSource)}
            </Typography>
          </>
        )}
      </TableCell>
      <TableCell data-testid={`${testid}-difference`}>{date.differenceS === null ? '—' : signed(date.differenceS)}</TableCell>
    </>
  )
}

export const StayDatesPanel: FC<{ events: EventRow[]; now: number }> = ({ events, now }) => {
  const translate = useTranslate()
  const { ago } = useDurations()
  const stays = stayDates(events, now)
  if (stays.length === 0) return null
  const title = translate('pages.scales.stay_dates.title')
  return (
    <Paper variant="outlined" role="region" aria-label={title} data-testid="stay-dates">
      <Typography variant="subtitle1" fontWeight="bold" px={2} pt={1.5}>
        {title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2}>{translate('pages.scales.stay_dates.muelle')}</TableCell>
            <TableCell colSpan={COLUMNS.length} align="center">
              {translate('pages.scales.stay_dates.arrival')}
            </TableCell>
            <TableCell colSpan={COLUMNS.length} align="center">
              {translate('pages.scales.stay_dates.departure')}
            </TableCell>
            <TableCell rowSpan={2}>{translate('pages.scales.stay_dates.phase')}</TableCell>
          </TableRow>
          <TableRow>
            {['arrival', 'departure'].flatMap(end =>
              COLUMNS.map(column => <TableCell key={`${end}-${column}`}>{translate(`pages.scales.stay_dates.${column}`)}</TableCell>),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {stays.map(stay => {
            const phase = phaseOf(stay)
            return (
              <TableRow
                key={stay.id}
                data-testid={`stay-dates-${stay.id}`}
                data-phase={phase}
                sx={phase === 'OVERDUE' ? { bgcolor: 'warning.light' } : undefined}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {stay.muelle}
                  </Typography>
                </TableCell>
                <DateCells date={stay.arrival} testid={`stay-dates-${stay.id}-arrival`} />
                <DateCells date={stay.departure} testid={`stay-dates-${stay.id}-departure`} />
                <TableCell>
                  <Chip
                    size="small"
                    color={PHASE_COLORS[phase]}
                    label={
                      stay.overdueS === null
                        ? translate(`pages.scales.stay_dates.phases.${phase}`)
                        : translate('pages.scales.stay_dates.phases.OVERDUE', { ago: ago(stay.overdueS) })
                    }
                    data-testid={`stay-dates-${stay.id}-phase`}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}
